import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Chip,
    Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getPaymentTransactionById } from '../../services/Api';

const ViewPaymentTransactionDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const response = await getPaymentTransactionById(id);
                if (response.success || response.data) {
                    setTransaction(response.data || response);
                } else {
                    setError(response?.message || 'Failed to fetch transaction details');
                }
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'An error occurred while fetching details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchDetails();
        }
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
    }

    if (!transaction) {
        return <Alert severity="warning" sx={{ mt: 4 }}>Transaction not found</Alert>;
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/payment-transactions');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        Transactions
                    </Link>
                    <Typography color="text.primary">Transaction Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Transaction #{transaction.id || id}
                    </Typography>
                    <Chip
                        label={transaction.status || 'unknown'}
                        color={
                            (transaction.status === 'completed' || transaction.status === 'success') ? 'success' :
                                (transaction.status === 'failed') ? 'error' :
                                    'warning'
                        }
                        sx={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '1rem', py: 2 }}
                    />
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Basic Information</Typography>
                        <Grid container spacing={2}>
                            <InfoRow label="Booking ID" value={transaction.bookingId} />
                            <InfoRow label="Service Type" value={transaction.serviceType} />
                            <InfoRow label="Amount" value={`₹${transaction.amount || 0}`} bold />
                            {transaction.gateway && <InfoRow label="Gateway" value={transaction.gateway} transform="capitalize" />}
                            <InfoRow label="Created At" value={transaction.createdAt || transaction.created_at ? new Date(transaction.createdAt || transaction.created_at).toLocaleString() : '-'} />
                            <InfoRow label="Updated At" value={transaction.updatedAt || transaction.updated_at ? new Date(transaction.updatedAt || transaction.updated_at).toLocaleString() : '-'} />
                        </Grid>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Payment Gateway Details</Typography>
                        <Grid container spacing={2}>
                            <InfoRow label="Order ID" value={transaction.razorpayOrderId || transaction.providerOrderId || '-'} breakAll />
                            <InfoRow label="Payment ID" value={transaction.razorpayPaymentId || transaction.providerTransactionId || transaction.transactionId || '-'} breakAll />
                            {transaction.razorpaySignature && (
                                <InfoRow label="Signature" value={transaction.razorpaySignature} breakAll />
                            )}
                            {transaction.paymentMethod && (
                                <InfoRow label="Payment Method" value={transaction.paymentMethod} transform="capitalize" />
                            )}
                            {transaction.currency && (
                                <InfoRow label="Currency" value={transaction.currency} />
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {transaction.errorDetails && (
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'error.main', borderRadius: 2, backgroundColor: '#fef2f2' }}>
                            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'error.main' }}>Error Details</Typography>
                            <Box sx={{
                                backgroundColor: 'white',
                                p: 2,
                                borderRadius: 1,
                                overflowX: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                color: 'error.dark'
                            }}>
                                <pre style={{ margin: 0 }}>
                                    {typeof transaction.errorDetails === 'object' ? JSON.stringify(transaction.errorDetails, null, 2) : transaction.errorDetails}
                                </pre>
                            </Box>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

// Helper component for uniform rows
const InfoRow = ({ label, value, breakAll = false, bold = false, transform = 'none' }) => (
    <>
        <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
            <Typography
                variant="body1"
                sx={{
                    fontWeight: bold ? 700 : 400,
                    wordBreak: breakAll ? 'break-all' : 'normal',
                    textTransform: transform
                }}
            >
                {value || '-'}
            </Typography>
            <Divider sx={{ mt: 1.5, display: { sm: 'none' } }} />
        </Grid>
    </>
);

export default ViewPaymentTransactionDetails;
